import type { useRender } from "@base-ui/react/use-render";
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { Badge } from "@replohq/lattice/components/badge/Badge";
import { Button } from "@replohq/lattice/components/button/Button";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";
import {
  BadgeCheck,
  BellRing,
  CircleAlert,
  Info,
  Megaphone,
  TriangleAlert,
  XIcon,
} from "lucide-react";

const alertVariants = cva(
  "relative w-full rounded-xl border border-border border-0.5 p-3 text-sm shadow-2xs",
  {
    variants: {
      variant: {
        default: "",
        error: "",
        warning: "",
        success: "",
        info: "",
        neutral: "",
        beta: "",
        critical:
          "border-rose-border bg-rose-soft [&_[data-slot=alert-title]]:text-rose [&_[data-slot=alert-description]]:text-rose [&_[data-slot=button]]:text-rose",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

export type AlertVariant = NonNullable<
  VariantProps<typeof alertVariants>["variant"]
>;

interface AlertRootProps<ClassName extends string = string> extends Omit<
  React.ComponentProps<"div">,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  variant?: AlertVariant;
}

function AlertRoot<ClassName extends string>({
  className,
  UNSAFE_className,
  variant,
  ...props
}: AlertRootProps<ClassName>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className, UNSAFE_className)}
      {...props}
    />
  );
}

interface AlertTitleProps<ClassName extends string = string> extends Omit<
  React.ComponentProps<"div">,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function AlertTitle<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: AlertTitleProps<ClassName>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 header-h6 tracking-tight",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface AlertDescriptionProps<ClassName extends string = string> extends Omit<
  React.ComponentProps<"div">,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function AlertDescription<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: AlertDescriptionProps<ClassName>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-default col-start-2 text-sm [&_p]:leading-relaxed break-words min-w-0",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

const variantIconConfig: Record<
  AlertVariant,
  {
    icon: React.ComponentType<{ size?: string | number; className?: string }>;
    iconClassName: string;
    containerClassName: string;
  } | null
> = {
  default: null,
  success: {
    icon: BadgeCheck,
    iconClassName: "text-green",
    containerClassName: "bg-green-soft border-green-border",
  },
  error: {
    icon: CircleAlert,
    iconClassName: "text-rose",
    containerClassName: "bg-rose-soft border-rose-border",
  },
  warning: {
    icon: TriangleAlert,
    iconClassName: "text-amber",
    containerClassName: "bg-amber-soft border-amber-border",
  },
  info: {
    icon: Info,
    iconClassName: "text-sky",
    containerClassName: "bg-sky-soft border-sky-border",
  },
  neutral: {
    icon: BellRing,
    iconClassName: "text-muted",
    containerClassName: "bg-surface border-border",
  },
  beta: {
    icon: Megaphone,
    iconClassName: "text-fuchsia",
    containerClassName: "bg-fuchsia-soft border-fuchsia-border",
  },
  critical: {
    icon: CircleAlert,
    iconClassName: "text-rose",
    containerClassName: "border-transparent bg-transparent shadow-none",
  },
};

interface AlertIconProps<ClassName extends string = string> extends Omit<
  useRender.ComponentProps<"span">,
  "className"
> {
  variant: AlertVariant;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function AlertIcon<ClassName extends string>({
  variant,
  className,
  UNSAFE_className,
  ...props
}: AlertIconProps<ClassName>) {
  const config = variantIconConfig[variant];
  if (!config) {
    return null;
  }
  const Icon = config.icon;

  return (
    <Badge
      size="xl"
      className={cn(config.containerClassName, className, UNSAFE_className)}
      {...props}
    >
      <Icon className={config.iconClassName} />
    </Badge>
  );
}

type ActionProps = {
  text: string;
  onClick: () => void;
};

interface AlertProps<ClassName extends string = string> extends Omit<
  React.ComponentProps<"div">,
  "className" | "title"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  variant?: AlertVariant;
  size?: "inline" | "multiline";
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ActionProps;
  onClose?: () => void;
}

function Alert<ClassName extends string>({
  className,
  UNSAFE_className,
  variant = "neutral",
  size = "inline",
  title,
  description,
  action,
  onClose,
  ...props
}: AlertProps<ClassName>) {
  const actionButton = action && (
    <Button
      size={size === "inline" ? "sm" : "base"}
      variant={size === "inline" ? "secondary" : "link"}
      onClick={action.onClick}
    >
      {action.text}
    </Button>
  );

  const closeButton = onClose && (
    <Button icon size="sm" variant="tertiary" onClick={onClose}>
      <XIcon />
    </Button>
  );

  return (
    <AlertRoot
      variant={variant}
      UNSAFE_className={cn(
        "flex gap-3",
        size === "inline" ? "items-center w-full h-fit" : "items-start w-75",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      <AlertIcon variant={variant} />

      {size === "inline" ? (
        <div className="flex justify-between items-center gap-3 w-full">
          <div className="flex flex-col">
            {title && <AlertTitle>{title}</AlertTitle>}
            {description && <AlertDescription>{description}</AlertDescription>}
          </div>

          <div className="flex gap-3 items-center">
            {actionButton}
            {closeButton}
          </div>
        </div>
      ) : (
        <div className="flex gap-2 justify-between w-full min-w-0">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex flex-col gap-1">
              {title && <AlertTitle>{title}</AlertTitle>}
              {description && (
                <AlertDescription>{description}</AlertDescription>
              )}
            </div>
            {actionButton}
          </div>
          {closeButton}
        </div>
      )}
    </AlertRoot>
  );
}

export { Alert, AlertIcon };
